import React, { useEffect, useState } from 'react';
import { get, post } from '../../../Api/http/httpClient';
import { Row, Card, Typography, Button, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import NotificationSettings from '../NotificationSettings/NotificationSettings';
import { CheckOutlined, WhatsAppOutlined, InstagramOutlined, SettingOutlined } from '@ant-design/icons';
import './WhatsAppSignup.css';

const { Title, Text } = Typography;

interface WhatsAppSignupProps {
  user_data: any;
}

const REDIRECT_URI = 'https://www.bloxai.co/dashboard/whatsapp-integration';

const WhatsAppSignup: React.FC<WhatsAppSignupProps> = ({ user_data }) => {
  const establishmentID = user_data["custom:establishment_id"];
  const family_name = user_data.family_name;
  const email = user_data.email;

  const [messageInfo, setMessageInfo] = useState<any>(null);
  const [phoneNumberId, setPhoneNumberId] = useState<string | null>(null);
  const [wabaId, setWabaId] = useState<string | null>(null);
  // Estados para el GET
  const [instagramBusinessId, setInstagramBusinessId] = useState<string | null>(null);
  const [paymentRequired, setPaymentRequired] = useState<boolean>(false);
  const [whatsappAssigned, setWhatsappAssigned] = useState<boolean>(false);
  const [whatsappNumber, setWhatsappNumber] = useState<string | null>(null);
  const [instagramAssigned, setInstagramAssigned] = useState<boolean>(false);
  const [instagramPageName, setInstagramPageName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const navigate = useNavigate();

  const [showEmail, setShowEmail] = useState(false);
  const handleButtonClick = () => {
    // Abre el cliente de correo
    window.location.href = "mailto:desarrollo@stelliontech.com";
    // Muestra el correo para copiar
    setShowEmail(true);
  };

  // Efecto para llamar al GET y verificar pago/asignación
  useEffect(() => {
    const fetchAssignedStatus = async () => {
      try {
        const url = `/check-assigned-number?establishmentID=${establishmentID}&name_establishment=${family_name}&email=${email}`;
        const response = await get(url);
        console.log('GET response:', response);
        
        if (response.answer === false) {
          setPaymentRequired(true);
        } else {
          setWhatsappAssigned(response.whatsapp_assigned);
          setWhatsappNumber(response.whatsapp_number);
          setInstagramAssigned(response.instagram_assigned);
          setInstagramPageName(response.instagram_page_name);
        }
      } catch (error: any) {
        console.error('Error fetching assigned status:', error);
        if (error.response && error.response.status === 403) {
          setPaymentRequired(true);
        }
      } finally {
        setLoading(false);
      }
    };
  
    fetchAssignedStatus();
  }, [establishmentID, family_name, email]);

  // Efecto para inicializar el SDK de Facebook y agregar listener al mensaje del Embedded Signup
  useEffect(() => {
    (window as any).fbAsyncInit = function () {
      (window as any).FB.init({
        appId: '1675667759889450',
        autoLogAppEvents: true,
        xfbml: true,
        version: 'v22.0',
      });
      console.log('Facebook SDK initialized');
    };

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.facebook.com') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          console.log('[WA_EMBEDDED_SIGNUP] =>', data);
          setMessageInfo(data);
          if (data.event === 'FINISH') {
            const phone = data.data.phone_number_id;
            const waba = data.data.waba_id;
            setPhoneNumberId(phone);
            setWabaId(waba);
            console.log('FINISH => phone_number_id:', phone, ' | waba_id:', waba);
            // Enviar datos al backend vía POST
            sendToBackend(phone, waba);
          } else if (data.event === 'CANCEL') {
            console.warn('Usuario canceló en el paso:', data.data.current_step);
          } else if (data.event === 'ERROR') {
            console.error('Error en Embedded Signup:', data.data.error_message);
          }
        }
      } catch (error) {
        console.error('Error procesando mensaje:', error);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Función para enviar datos al backend (POST)
  const sendToBackend = async (phone: string, waba: string) => {
    try {
      const response = await post('/check-assigned-number', {
        establishmentID,
        wabaId: waba,
        phoneNumberId: phone,
        name_establishment: family_name,
        emailEstablishment: email,
      });
      console.log('Respuesta del backend:', response);
      setMessageInfo(response);
      setWhatsappAssigned(true); // Se cambia el estado a true para que al usuario le muestre el mensaje de éxito
    } catch (error) {
      console.error('Error enviando datos al backend:', error);
    }
  };

  // Función para iniciar la integración mediante Facebook
  const launchWhatsAppSignup = () => {
    const FB = (window as any).FB;
    if (!FB) {
      console.error('El objeto FB no está disponible. Asegúrate de que el SDK se haya cargado.');
      return;
    }
    FB.login(
      (response: any) => {
        if (response.authResponse) {
          console.log('Usuario autenticado en Facebook');
        } else {
          console.warn('Usuario canceló login o no autorizó.');
        }
      },
      {
        config_id: '932046389045772',
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: '',
          sessionInfoVersion: '2',
        },
      }
    );
  };
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      console.log("📥 Código recibido de Instagram:", code);
      // Enviar al backend
      sendCodeToBackend(code);
    }
  }, [searchParams]);

  const sendCodeToBackend = async (code: string) => {
    try {
      const payload = {
        establishmentID,
        name_establishment: family_name,
      };
      const response = await post('/instagram', payload);
    } catch (error) {
      console.error("❌ Error enviando code: ", error);
    }
  };

  // Función para desconectar WhatsApp e Instagram
  const disconnectWhatsApp = async () => {
    try {
      const response = await post('/disconnect-whatsapp', {
        establishmentID,
      });
      console.log('WhatsApp desintegrado:', response);
      // Opcional: volver al estado inicial
      setWhatsappAssigned(false);
      setPhoneNumberId(null);
      setWabaId(null);
    } catch (error) {
      console.error('Error al desintegrar WhatsApp:', error);
    }
  };
  
  const disconnectInstagram = async () => {
    try {
      const response = await post('/disconnect-instagram', {
        establishmentID,
      });
      console.log('Instagram desintegrado:', response);
      // Opcional: limpiar estados relacionados con Instagram
      setInstagramBusinessId(null);
    } catch (error) {
      console.error('Error al desintegrar Instagram:', error);
    }
  };

  // Mostrar el spinner mientras se carga la información
  if (loading) {
    return (
      <Row justify="center" align="middle" style={{ height: "100vh", textAlign: "center" }}>
        <Spin size="large" />
      </Row>
    );
  }

  // Si el usuario no ha pagado, se muestra el mensaje correspondiente
  if (paymentRequired) {
    return (
      <Row justify="center" align="middle" style={{ height: "100vh", textAlign: "center" }}>
        <Card
          style={{
            textAlign: "center",
            padding: "30px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            borderRadius: "10px",
          }}
        >
          <Title level={3} style={{ color: "#ff4d4f" }}>
            ¡No has pagado!
          </Title>
          <p style={{ fontSize: "16px", lineHeight: "1.5" }}>
            Debido a que no has completado el pago, no podrás realizar la integración con WhatsApp.
            Por favor, elige un plan adecuado para habilitar esta funcionalidad.
          </p>
          <p style={{ fontSize: "14px", lineHeight: "1.5", marginTop: "10px" }}>
            <strong>¿Ya pagaste?</strong> Intenta cerrando sesión y volviendo a iniciar.
          </p>
          <p style={{ fontSize: "14px", lineHeight: "1.5", marginTop: "10px" }}>
            Si el problema persiste, comunícate con nosotros para obtener ayuda.
          </p>
          <Button
            type="primary"
            size="large"
            style={{ marginTop: "20px" }}
            onClick={() => navigate("/plans")}
          >
            Ver Planes
          </Button>
          <Button
            type="primary"
            size="large"
            style={{ marginTop: "10px", marginLeft: "10px" }}
            onClick={handleButtonClick}
          >
            Contactar Soporte
          </Button>
          {showEmail && (
            <div
              style={{
                marginTop: "10px",
                padding: "10px",
                border: "1px solid #d9d9d9",
                borderRadius: "10px",
                backgroundColor: "#f5f5f5",
                display: "inline-block",
                marginLeft: "10px"
              }}
            >
              <Text
                style={{
                  display: "block",
                  cursor: "pointer",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
                copyable
              >
                desarrollo@stelliontech.com
              </Text>
            </div>
          )}
        </Card>
      </Row>
    );
  }

  // Si el pago es válido pero aún no se ha asignado el número, se muestra el botón para iniciar la integración
  if (!whatsappAssigned) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Title level={2}>Integración con WhatsApp</Title>
        <p style={{ fontSize: '16px' }}>
          Por favor, inicia sesión con Facebook para comenzar la integración.
        </p>
        <Button
          type="primary"
          size="large"
          onClick={launchWhatsAppSignup}
          style={{
            backgroundColor: '#1877f2',
            border: 0,
            borderRadius: '4px',
            color: '#fff',
            fontSize: '16px',
            fontWeight: 'bold',
            height: '40px',
            padding: '0 24px',
            marginBottom: '16px',
          }}
        >
          Iniciar Integración con Facebook
        </Button>
      </div>
    );
  }

  // Si el pago es válido y ya se asignó el número, se muestra un mensaje de integración exitosa
  return (
    <div className="whatsapp-signup-container">
      <div className="whatsapp-signup-card">
        <div className="whatsapp-signup-content">
          <div className="whatsapp-success-icon">
            <CheckOutlined />
          </div>

          <Title level={2} className="whatsapp-success-title">
            ¡Integración Exitosa!
          </Title>

          <div className="whatsapp-status-section">
            <div className="whatsapp-status-card">
              <div className="whatsapp-status-header">
                <div className="whatsapp-status-icon">
                  <CheckOutlined />
                </div>
                <Title level={4} className="whatsapp-status-title">
                  WhatsApp Business Integrado
                </Title>
              </div>
              <p className="whatsapp-status-text">
                Tu cuenta de WhatsApp está configurada y lista para usar. Ahora puedes gestionar tus conversaciones y mantener una comunicación efectiva con tus clientes.
              </p>
              {whatsappNumber && (
                <div className="whatsapp-assigned-info">
                  <Text strong>Número asignado:</Text>
                  <a 
                    href={`https://wa.me/${whatsappNumber}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="whatsapp-number-link"
                  >
                    {whatsappNumber}
                  </a>
                </div>
              )}
            </div>

            {!instagramAssigned ? (
              <div className="whatsapp-next-step-card">
                <Title level={4} className="whatsapp-next-step-title">
                  Siguiente paso: Integrar Instagram
                </Title>
                <p className="whatsapp-next-step-text">
                  Completa tu integración conectando tu cuenta de Instagram Business para gestionar todos tus canales de comunicación en un solo lugar.
                </p>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    window.location.href =
                      'https://www.instagram.com/oauth/authorize?enable_fb_login=0&force_authentication=1&client_id=946782704099522&redirect_uri=https://www.bloxai.co/dashboard/whatsapp-integration&response_type=code&scope=instagram_business_basic,instagram_business_manage_messages,instagram_business_manage_comments,instagram_business_content_publish,instagram_business_manage_insights';
                  }}
                  className="whatsapp-instagram-button"
                >
                  Integrar Instagram Business
                </Button>
              </div>
            ) : (
              <div className="whatsapp-status-card">
                <div className="whatsapp-status-header">
                  <div className="whatsapp-status-icon">
                    <CheckOutlined />
                  </div>
                  <Title level={4} className="whatsapp-status-title">
                    Instagram Business Integrado
                  </Title>
                </div>
                <p className="whatsapp-status-text">
                  Tu cuenta de Instagram Business está configurada y lista para usar. Ahora puedes gestionar tus mensajes desde un solo lugar.
                </p>
                {instagramPageName && (
                  <div className="whatsapp-assigned-info">
                    <Text strong>Página de Instagram:</Text>
                    <a 
                      href={`https://instagram.com/${instagramPageName}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="instagram-page-link"
                    >
                      {instagramPageName}
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="whatsapp-connections-card">
              <Title level={4} className="whatsapp-connections-title">
                <SettingOutlined />
                Gestionar Conexiones
              </Title>
              <p className="whatsapp-connections-text">
                Aquí puedes gestionar tus conexiones con WhatsApp e Instagram. Ten en cuenta que al desconectar una plataforma, perderás acceso a sus funcionalidades.
              </p>
              <div className="whatsapp-connections-buttons">
                <Button
                  danger
                  onClick={disconnectWhatsApp}
                  className="whatsapp-disconnect-button"
                  disabled
                >
                  <WhatsAppOutlined />
                  Desconectar WhatsApp
                </Button>
                <Button
                  danger
                  onClick={disconnectInstagram}
                  className="whatsapp-disconnect-button"
                  disabled
                >
                  <InstagramOutlined />
                  Desconectar Instagram
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <NotificationSettings user_data={user_data} />
      
    </div>
  );
};

export default WhatsAppSignup;
